/**
 * Tilemaps catalog (TM3). Lists the project's tile maps from the reactive
 * catalog store with open / add-to-current-graph / rename / delete, plus a
 * create form. "Add to graph" drops a `tilemap_ref` node wired to the map so
 * the story graph can reference it (the connection between the graph and the
 * tilemap editor — the original ask). Replaces TM1's minimal opener.
 */

import { useEffect, useState } from 'react';
import { tilemapClient } from '../api/tilemapClient';
import { catalogStore, useCatalog } from '../store/catalogStore';
import { useProjectStore } from '../../graph/store/projectStore';

export function TilemapMenu({
    projectUuid,
    onOpen,
    onClose,
}: {
    projectUuid: string;
    onOpen: (tilemapUuid: string) => void;
    onClose: () => void;
}) {
    const maps = useCatalog((s) => s.byProject[projectUuid]);
    const currentGraph = useProjectStore((s) => s.currentGraph);
    const addNodeAt = useProjectStore((s) => s.addNodeAt);

    const [name, setName] = useState('New Tile Map');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => { catalogStore.getState().ensure(projectUuid); }, [projectUuid]);

    const create = async () => {
        setBusy(true);
        setError(null);
        try {
            const doc = await tilemapClient.createTilemap(projectUuid, { name: name.trim() || 'New Tile Map' });
            await catalogStore.getState().refresh(projectUuid);
            onOpen(doc.tilemap.uuid);
        } catch (e) {
            setError((e as Error).message);
            setBusy(false);
        }
    };

    const rename = async (uuid: string, current: string) => {
        const next = prompt('Rename tile map', current);
        if (next == null || next.trim() === '' || next === current) return;
        await tilemapClient.updateTilemap(projectUuid, uuid, { name: next.trim() });
        await catalogStore.getState().refresh(projectUuid);
    };

    const remove = async (uuid: string, mapName: string) => {
        if (!confirm(`Delete tile map "${mapName}"? Referencing nodes will show a placeholder.`)) return;
        try {
            await tilemapClient.deleteTilemap(projectUuid, uuid, true);
            await catalogStore.getState().refresh(projectUuid);
        } catch (e) {
            setError((e as Error).message);
        }
    };

    const addToGraph = (uuid: string) => {
        if (!currentGraph) { setError('Open a graph first to add a node.'); return; }
        // Drop the node near the canvas origin; the user can reposition it.
        addNodeAt('tilemap_ref', { x: 80, y: 80 }, { tilemap_uuid: uuid });
        onClose();
    };

    return (
        <div className="modal modal-blur show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.4)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title"><i className="ti ti-grid-4x4 me-2" />Tile Maps</h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger py-2">{error}</div>}

                        {!maps ? (
                            <div className="text-secondary small">Loading…</div>
                        ) : maps.length === 0 ? (
                            <div className="text-secondary small fst-italic mb-3">No tile maps yet.</div>
                        ) : (
                            <div className="list-group mb-3" style={{ maxHeight: 320, overflow: 'auto' }}>
                                {maps.map((m) => (
                                    <div key={m.uuid} className="list-group-item d-flex align-items-center gap-2">
                                        <div className="flex-fill">
                                            <div className="fw-semibold">{m.name}</div>
                                            <div className="text-secondary small">{m.layers_count} layers · {m.tiles_painted} tiles</div>
                                        </div>
                                        <button className="btn btn-sm btn-primary" onClick={() => onOpen(m.uuid)}>Open</button>
                                        <button className="btn btn-sm btn-outline-secondary" disabled={!currentGraph} title={currentGraph ? 'Add a reference node to the current graph' : 'Open a graph first'} onClick={() => addToGraph(m.uuid)}>
                                            Add to graph
                                        </button>
                                        <button className="btn btn-sm btn-ghost-secondary" title="Rename" onClick={() => rename(m.uuid, m.name)}><i className="ti ti-pencil" /></button>
                                        <button className="btn btn-sm btn-ghost-danger" title="Delete" onClick={() => remove(m.uuid, m.name)}><i className="ti ti-trash" /></button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="d-flex gap-2 align-items-end border-top pt-3">
                            <div className="flex-fill">
                                <label className="form-label small mb-0">New map</label>
                                <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="Map name" />
                            </div>
                            <button className="btn btn-sm btn-primary" disabled={busy} onClick={create}>
                                {busy ? 'Creating…' : 'Create & open'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
